/* ════════════════════════════════════════════════════════════════════
   PERISAI DOM — halaman tidak lagi tumbang bila benda luar mengusiknya
   ════════════════════════════════════════════════════════════════════

   MASALAH YANG IA SELESAIKAN (aduan pelanggan 2026-09-01, "ramai kena"):
   skrin "Component Crash — NotFoundError: Failed to execute 'removeChild'
   on 'Node': The node to be removed is not a child of this node" pada
   /workspace, berulang SETIAP KALI halaman dibuka.

   PUNCANYA BUKAN KOD KITA. React menyimpan rujukan kepada setiap nod yang
   ia cipta. Bila pihak ketiga MENGGANTIKAN nod itu dengan nodnya sendiri —
   terjemahan halaman Chrome (ia membungkus setiap teks dalam <font>),
   extension muat turun video, pelindung pelayaran antivirus, Grammarly —
   rujukan React menjadi basi. Kemas kini skrin seterusnya memanggil
   `induk.removeChild(anak)` pada anak yang sudah tiada di situ, browser
   melempar NotFoundError, dan React merobohkan seluruh subpokok.

   ⛔ Tiada jumlah pembetulan pada kod kita boleh menghalangnya — kita
      tidak mengawal browser pelanggan. Yang boleh kita buat ialah tidak
      MATI kerananya. Itulah fungsi fail ini.

   Perisai menjadikan dua operasi itu tidak berbahaya: kalau nod yang
   hendak dibuang (atau nod rujukan bagi sisipan) bukan lagi anak kepada
   induk yang disebut, operasi itu DILANGKAU dan nilai dipulangkan
   sebagaimana adanya, bukan dilemparkan. React meneruskan kerjanya; paling
   teruk, secebis teks yang sudah diganti pihak ketiga kekal sebentar di
   skrin. Halaman kekal hidup.

   ⭐⭐ IA JUGA PENDERIA — dan inilah sebab kaunter di bawah WAJIB ada.
      Sebaik perisai berfungsi, kemalangan BERHENTI berlaku. Maknanya log
      kemalangan akan senyap, dan kita takkan pernah tahu berapa ramai
      sebenarnya yang terkesan atau extension mana yang menjadi punca.
      Kaunter ini menukar kemalangan yang hilang itu menjadi ukuran:
      setiap kali perisai bertindak ia mengira, dan merakam NAMA nod yang
      terlibat — nama itulah yang menuding kepada extension tertentu.

   ⚠️ Ia menampal `Node.prototype`, jadi ia menyentuh SELURUH halaman. Ia
      dipasang SEKALI sahaja, sebelum React mount, dan tidak pernah
      dibuang. Kos prestasi = satu perbandingan rujukan bagi setiap
      operasi — tidak boleh diukur.

   ⚠️ HARGA YANG DITERIMA SECARA SEDAR: kalau kod KITA sendiri suatu hari
      memanggil removeChild pada induk yang salah, perisai ini akan
      menyembunyikan pepijat itu daripada menjadi kemalangan. Sebab itu
      setiap pintasan direkod dan dihantar ke `error_logs` — ia disenyapkan
      di skrin, tetapi TIDAK disenyapkan dalam laporan.
*/

/* Kaunter hidup selama hayat halaman. Ia sengaja BUKAN state React —
   perisai dipasang sebelum React wujud, dan ia mesti terus mengira
   walaupun pokok React sedang roboh. */
const kiraan = {
    removeChild: 0,
    insertBefore: 0,
    /* Huraian nod PERTAMA yang mencetuskan perisai. Yang pertama paling
       bernilai: ia berlaku sebelum keadaan halaman menjadi kucar-kacir,
       jadi ia paling hampir kepada punca sebenar. */
    contohPertama: null,
};

let sudahDipasang = false;

/* Nama pendek bagi satu nod, untuk laporan. Bentuk: `tag#id.kelas`.
   Dipangkas pendek kerana ia masuk ke dalam dokumen log, bukan untuk
   dibaca mesin. Dibalut try/catch: nod yang sudah separuh dibuang boleh
   melempar pada akses harta yang paling biasa sekalipun. */
function huraiNod(n) {
    try {
        if (!n) return 'null';
        if (n.nodeType === 3) return `#teks("${String(n.nodeValue || '').trim().slice(0, 24)}")`;
        if (n.nodeType !== 1) return `#nod${n.nodeType}`;
        const tag = (n.tagName || '?').toLowerCase();
        const id = n.id ? `#${String(n.id).slice(0, 32)}` : '';
        const kelas = typeof n.className === 'string' && n.className
            ? `.${n.className.trim().split(/\s+/).slice(0, 2).join('.').slice(0, 40)}`
            : '';
        return `${tag}${id}${kelas}`;
    } catch {
        return '?';
    }
}

function rakam(jenis, anak, induk) {
    kiraan[jenis] += 1;
    if (!kiraan.contohPertama) {
        kiraan.contohPertama = `${jenis}: ${huraiNod(anak)} @ ${huraiNod(induk)}`;
    }
}

/* Pasang perisai. Selamat dipanggil berulang kali — panggilan kedua
   tidak membuat apa-apa (kalau tidak, penampal akan membungkus penampal
   dan setiap operasi DOM melalui rantai yang makin panjang). */
export function pasangDomGuard() {
    if (sudahDipasang) return;
    if (typeof Node === 'undefined' || !Node.prototype) return;
    sudahDipasang = true;

    const removeChildAsal = Node.prototype.removeChild;
    Node.prototype.removeChild = function (anak) {
        if (anak && anak.parentNode !== this) {
            rakam('removeChild', anak, this);
            return anak;
        }
        return removeChildAsal.apply(this, arguments);
    };

    const insertBeforeAsal = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (nodBaru, nodRujukan) {
        if (nodRujukan && nodRujukan.parentNode !== this) {
            rakam('insertBefore', nodRujukan, this);
            return nodBaru;
        }
        return insertBeforeAsal.apply(this, arguments);
    };
}

export function bacaDomGuard() {
    return { ...kiraan, jumlah: kiraan.removeChild + kiraan.insertBefore };
}

export function adaGangguanDom() {
    return kiraan.removeChild + kiraan.insertBefore > 0;
}

/* ── CAP JARI PERSEKITARAN ─────────────────────────────────────────────
   Kaunter di atas memberitahu BAHAWA halaman diusik. Blok di bawah cuba
   menjawab OLEH SIAPA.

   Ia membaca sahaja — tiada apa diubah, tiada apa dibuang. Semua pemilih
   di bawah ialah tanda yang extension itu sendiri tinggalkan pada
   halaman; tiada data pengguna dibaca.

   ⛔ Senarai ini akan menjadi lapuk apabila extension bertukar rupa. Ia
      BUKAN pagar keselamatan dan tiada apa yang bergantung padanya —
      kalau ia terlepas sesuatu, `suntikanLuar` di bawah tetap menangkap
      nama nod itu secara mentah. Jadi jangan sekali-kali menjadikan
      keputusan produk bergantung pada senarai ini sahaja. */
const TANDA_EXTENSION = [
    ['terjemah-chrome', () => {
        const k = document.documentElement.classList;
        return k.contains('translated-ltr') || k.contains('translated-rtl');
    }],
    ['terjemah-google-ui', () => !!document.querySelector('.goog-te-banner-frame, #goog-gt-tt, .skiptranslate')],
    ['terjemah-edge', () => !!document.querySelector('[_msttexthash]')],
    ['grammarly', () => !!document.querySelector('grammarly-extension, [data-grammarly-shadow-root], .grammarly-desktop-integration')],
    ['idm', () => !!document.querySelector('#idmBtn, .idm_download_panel, #idmmzcdcont')],
    ['mcafee', () => !!document.querySelector('[id^="mcafee"], [class^="mcafee"]')],
    ['muat-turun-video', () => !!document.querySelector('[class*="downloadhelper" i], [id*="videodownload" i], [class*="savefrom" i]')],
];

/* Nod yang duduk terus di dalam <body> tetapi BUKAN milik kita.
   Ini penderia am yang paling berguna: ia menangkap extension yang tiada
   dalam senarai di atas, kerana hampir setiap extension yang menulis atas
   halaman meninggalkan sekurang-kurangnya satu nod di sini.

   `sesaat-automation-overlay` ialah overlay extension KITA sendiri —
   ia dikecualikan supaya kita tidak menyalahkan diri sendiri. */
function suntikanLuar() {
    const keluar = [];
    try {
        const anak = document.body ? Array.from(document.body.children) : [];
        for (const n of anak) {
            const tag = (n.tagName || '').toLowerCase();
            if (tag === 'script' || tag === 'style' || tag === 'link') continue;
            if (n.id === 'root' || n.id === 'sesaat-automation-overlay') continue;
            keluar.push(huraiNod(n));
            if (keluar.length >= 6) break;
        }
    } catch { /* body belum wujud / akses disekat — abai */ }
    return keluar;
}

/* Ringkasan untuk dilampirkan pada laporan. Semua medan pendek dan
   selamat: tiada teks pengguna, tiada kandungan produk. */
export function capJariDom() {
    const cap = { dikesan: [], suntikan: [], bahasaHalaman: null, bahasaBrowser: null };
    try {
        for (const [nama, uji] of TANDA_EXTENSION) {
            try { if (uji()) cap.dikesan.push(nama); } catch { /* pemilih ditolak — abai */ }
        }
        cap.suntikan = suntikanLuar();
        cap.bahasaHalaman = document.documentElement.getAttribute('lang') || null;
        cap.bahasaBrowser = (navigator.languages && navigator.languages[0]) || navigator.language || null;
    } catch { /* jangan sekali-kali melempar dari laluan laporan */ }
    return cap;
}
